import { useCallback } from "react";
import { useAuth } from "./useAuth";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

export function useApi() {
  const { token, refreshToken } = useAuth();

  const request = useCallback(
    async <T>(
      method: string,
      path: string,
      body?: unknown
    ): Promise<T> => {
      // Proactively refresh if the token is expired or within 60s of expiry,
      // so we avoid the 401 → refresh → retry round-trip in the common case.
      let activeToken = token;
      if (activeToken) {
        try {
          const exp = JSON.parse(atob(activeToken.split(".")[1])).exp as number;
          if (exp - Math.floor(Date.now() / 1000) < 60) {
            activeToken = await refreshToken();
          }
        } catch { /* malformed token — let the request proceed and fail naturally */ }
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (activeToken) headers["Authorization"] = `Bearer ${activeToken}`;

      const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      // Fallback refresh on unexpected 401 (e.g. clock skew)
      if (res.status === 401 && activeToken) {
        const newToken = await refreshToken();
        if (newToken) {
          const retryRes = await fetch(`${API_BASE}${path}`, {
            method,
            headers: { ...headers, Authorization: `Bearer ${newToken}` },
            body: body ? JSON.stringify(body) : undefined,
          });
          if (!retryRes.ok) throw new ApiError(retryRes.status, await retryRes.json());
          return retryRes.json();
        }
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new ApiError(res.status, err);
      }

      return res.json();
    },
    [token, refreshToken]
  );

  const get   = useCallback(<T>(path: string)               => request<T>("GET",    path),         [request]);
  const post  = useCallback(<T>(path: string, body: unknown) => request<T>("POST",   path, body),   [request]);
  const put   = useCallback(<T>(path: string, body: unknown) => request<T>("PUT",    path, body),   [request]);
  const patch = useCallback(<T>(path: string, body: unknown) => request<T>("PATCH",  path, body),   [request]);
  const del   = useCallback(<T>(path: string)               => request<T>("DELETE", path),         [request]);

  return { get, post, put, patch, del };
}

export class ApiError extends Error {
  constructor(public status: number, public body: { error?: string }) {
    super(body?.error ?? `HTTP ${status}`);
  }
}
